import { useEffect, useState } from 'react'

/**
 * Every open overlay, outermost first.
 *
 * The job card opens the activity sheet over itself and the booking dialog over
 * that, and three overlays all listening on `document` for Escape would close
 * every one of them at once — an inner listener cannot stop an event reaching a
 * sibling listener on the same node. So depth is tracked here and only the last
 * one in the stack answers the key.
 *
 * Shared by `Modal` and `Sheet` deliberately. Two stacks would each believe
 * they were the top of the world.
 */
const stack: symbol[] = []

/**
 * Escape, scroll lock, and the depth an overlay sits at.
 *
 * The returned depth is what keeps a dialog above the sheet that opened it,
 * because both are portalled to `body` and neither is inside the other's DOM.
 */
export function useOverlay(open: boolean, onClose: () => void): number {
  const [token] = useState(() => Symbol('overlay'))
  const [depth, setDepth] = useState(0)

  useEffect(() => {
    if (!open) return

    stack.push(token)
    setDepth(stack.length - 1)

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (stack[stack.length - 1] !== token) return
      event.stopPropagation()
      onClose()
    }

    document.addEventListener('keydown', onKeyDown)

    // Stops the page scrolling behind the overlay, which otherwise loses the
    // dispatcher's place while they read. Only the outermost sets it, so
    // closing an inner dialog does not hand scrolling back too early.
    const { overflow } = document.body.style
    if (stack.length === 1) document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const index = stack.indexOf(token)
      if (index !== -1) stack.splice(index, 1)
      if (stack.length === 0) document.body.style.overflow = overflow
    }
  }, [open, onClose, token])

  return depth
}
